import { useState } from 'react';

interface Job {
  id: string;
  title: string;
  department: string;
  location: string;
  type: string;
  description: string;
}

export default function OpenPositions() {
  const [selectedDepartment, setSelectedDepartment] = useState('All');
  const [selectedLocation, setSelectedLocation] = useState('All');

  const jobs: Job[] = [
    {
      id: '1',
      title: 'Senior Software Engineer',
      department: 'Technology',
      location: 'Paris, France',
      type: 'Full-time',
      description: 'Build scalable systems for our global tracking platform using React, Node.js, and cloud technologies.'
    },
    {
      id: '2',
      title: 'DevOps Engineer',
      department: 'Technology',
      location: 'Remote',
      type: 'Full-time',
      description: 'Manage our cloud infrastructure, CI/CD pipelines, and ensure 99.9% uptime for our global services.'
    },
    {
      id: '3',
      title: 'Data Scientist',
      department: 'Technology',
      location: 'Singapore',
      type: 'Full-time',
      description: 'Develop AI models for route optimization, demand forecasting, and predictive analytics.'
    },
    {
      id: '4',
      title: 'Operations Manager',
      department: 'Operations',
      location: 'Dubai, UAE',
      type: 'Full-time',
      description: 'Oversee daily operations at our Middle East hub, managing teams and ensuring service excellence.'
    },
    {
      id: '5',
      title: 'Warehouse Supervisor',
      department: 'Operations',
      location: 'Los Angeles, USA',
      type: 'Full-time',
      description: 'Lead warehouse operations, inventory management, and coordinate with logistics teams.'
    },
    {
      id: '6',
      title: 'Logistics Coordinator',
      department: 'Operations',
      location: 'Hamburg, Germany',
      type: 'Full-time',
      description: 'Coordinate shipments, manage documentation, and ensure timely delivery across European routes.'
    },
    {
      id: '7',
      title: 'Customs Specialist',
      department: 'Operations',
      location: 'Rotterdam, Netherlands',
      type: 'Full-time',
      description: 'Handle customs clearance, compliance documentation, and regulatory requirements for international shipments.'
    },
    {
      id: '8',
      title: 'Account Executive',
      department: 'Sales',
      location: 'New York, USA',
      type: 'Full-time',
      description: 'Build relationships with enterprise clients, identify new business opportunities, and drive revenue growth.'
    },
    {
      id: '9',
      title: 'Business Development Manager',
      department: 'Sales',
      location: 'London, UK',
      type: 'Full-time',
      description: 'Expand our European market presence, develop strategic partnerships, and achieve sales targets.'
    },
    {
      id: '10',
      title: 'Customer Success Manager',
      department: 'Customer Support',
      location: 'Remote',
      type: 'Full-time',
      description: 'Ensure customer satisfaction, provide onboarding support, and maintain long-term client relationships.'
    },
    {
      id: '11',
      title: 'Support Specialist',
      department: 'Customer Support',
      location: 'Ajaccio, France',
      type: 'Full-time',
      description: 'Provide exceptional customer support via phone, email, and chat. Multilingual skills preferred.'
    },
    {
      id: '12',
      title: 'Product Manager',
      department: 'Product',
      location: 'San Francisco, USA',
      type: 'Full-time',
      description: 'Define product strategy, prioritize features, and work with engineering teams to deliver innovative solutions.'
    },
    {
      id: '13',
      title: 'UX/UI Designer',
      department: 'Product',
      location: 'Remote',
      type: 'Full-time',
      description: 'Design intuitive interfaces for our tracking platform, mobile apps, and customer portals.'
    },
    {
      id: '14',
      title: 'Marketing Manager',
      department: 'Marketing',
      location: 'Sydney, Australia',
      type: 'Full-time',
      description: 'Lead marketing campaigns, manage brand strategy, and drive customer acquisition in the Asia-Pacific region.'
    },
    {
      id: '15',
      title: 'Content Strategist',
      department: 'Marketing',
      location: 'Remote',
      type: 'Full-time',
      description: 'Create compelling content for our blog, social media, and marketing materials. SEO expertise required.'
    },
    {
      id: '16',
      title: 'Financial Analyst',
      department: 'Finance',
      location: 'Zurich, Switzerland',
      type: 'Full-time',
      description: 'Analyze financial data, prepare reports, and support strategic decision-making for global operations.'
    },
    {
      id: '17',
      title: 'HR Business Partner',
      department: 'Human Resources',
      location: 'Tokyo, Japan',
      type: 'Full-time',
      description: 'Support talent acquisition, employee development, and HR initiatives across our Asian operations.'
    },
    {
      id: '18',
      title: 'Sustainability Manager',
      department: 'Sustainability',
      location: 'Copenhagen, Denmark',
      type: 'Full-time',
      description: 'Lead our carbon-neutral initiatives, develop green logistics strategies, and drive environmental impact.'
    }
  ];

  const departments = ['All', 'Technology', 'Operations', 'Sales', 'Customer Support', 'Product', 'Marketing', 'Finance', 'Human Resources', 'Sustainability'];
  const locations = ['All', 'Remote', 'Paris, France', 'Singapore', 'Dubai, UAE', 'Los Angeles, USA', 'Hamburg, Germany', 'Rotterdam, Netherlands', 'New York, USA', 'London, UK', 'Ajaccio, France', 'San Francisco, USA', 'Sydney, Australia', 'Zurich, Switzerland', 'Tokyo, Japan', 'Copenhagen, Denmark'];

  const filteredJobs = jobs.filter(job => {
    const matchesDepartment = selectedDepartment === 'All' || job.department === selectedDepartment;
    const matchesLocation = selectedLocation === 'All' || job.location === selectedLocation;
    return matchesDepartment && matchesLocation;
  });

  return (
    <section id="positions" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Open Positions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore opportunities across our global offices and departments. Find the perfect role to advance your career.
          </p>
        </div>

        <div className="flex flex-wrap gap-4 mb-12">
          <div className="flex-1 min-w-[250px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Department
            </label>
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
            >
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-[250px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location
            </label>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
            >
              {locations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mb-6 text-gray-600">
          Showing <strong>{filteredJobs.length}</strong> {filteredJobs.length === 1 ? 'position' : 'positions'}
        </div>

        <div className="space-y-4">
          {filteredJobs.map(job => (
            <div 
              key={job.id}
              className="bg-white p-6 rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1 min-w-[300px]">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {job.title}
                  </h3>
                  <div className="flex flex-wrap gap-3 mb-3">
                    <span className="inline-flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-building-line"></i>
                      {job.department}
                    </span>
                    <span className="inline-flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-map-pin-line"></i>
                      {job.location}
                    </span>
                    <span className="inline-flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-time-line"></i>
                      {job.type}
                    </span>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    {job.description}
                  </p>
                </div>
                <button className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer">
                  Apply Now
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredJobs.length === 0 && (
          <div className="text-center py-16">
            <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-xl text-gray-500">No positions found matching your criteria.</p>
            <p className="text-gray-400 mt-2">Try adjusting your filters or check back later for new opportunities.</p>
          </div>
        )}

        <div className="mt-16 bg-blue-50 rounded-xl p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Don't See the Right Role?
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            We're always looking for talented individuals. Send us your resume and we'll keep you in mind for future opportunities.
          </p>
          <button className="bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer">
            Submit Your Resume
          </button>
        </div>
      </div>
    </section>
  );
}
